import { DELAY } from '@vuejs/helpers/_constants'
import { appendRandomString } from '@vuejs/utils/stringUtils'
import {
	computed,
	readonly,
	ref,
} from 'vue'

import type { CANotificationItem } from '../interfaces'

const notifications = ref<CANotificationItem[]>([])

const createNotification = (notification: CANotificationItem): CANotificationItem & { id: string } => {
	return {
		buttonText: notification.buttonText,
		dataTest: notification.dataTest,
		duration: notification.duration || DELAY.FIVE_SECONDS,
		id: `notification-${appendRandomString()}`,
		message: notification.message,
		showCloseButton: notification.showCloseButton,
		type: notification.type,
	}
}

export const useNotifications = () => {
	const addNotification = (notification: CANotificationItem | CANotificationItem[]): void => {
		const notificationsToAdd = Array.isArray(notification) ? notification : [notification]

		const notificationsWithDefaults = notificationsToAdd.map(createNotification)

		notifications.value.push(...notificationsWithDefaults)
	}

	const clearNotifications = (): void => {
		notifications.value = []
	}

	const removeNotification = (notification: CANotificationItem): void => {
		const index = notifications.value.findIndex(n => n.id === notification.id)

		if (index > -1) {
			notifications.value.splice(index, 1)
		}
	}

	const errorNotifications = computed(() =>
		notifications.value.filter(n => n.type === 'error')
	)

	const infoNotifications = computed(() =>
		notifications.value.filter(n => n.type === 'info')
	)

	const successNotifications = computed(() =>
		notifications.value.filter(n => n.type === 'success')
	)

	return {
		addNotification,
		clearNotifications,
		errorNotifications: readonly(errorNotifications),
		infoNotifications: readonly(infoNotifications),
		notifications: readonly(notifications),
		removeNotification,
		successNotifications: readonly(successNotifications),
	}
}

const notificationComposable = useNotifications()

const createTypedNotificationMethod = (type: CANotificationItem['type']) => {
	return (notification: CANotificationItem | Omit<CANotificationItem, 'type'>): void => {
		notificationComposable.addNotification({
			...notification,
			type,
		} as CANotificationItem)
	}
}

export const NotificationService = {
	useNotifications: () => notificationComposable,

	methods: {
		clearAll() {
			notificationComposable.clearNotifications()
		},

		dismiss(notification: CANotificationItem) {
			notificationComposable.removeNotification(notification)
		},

		emitNotification(notification: CANotificationItem) {
			notificationComposable.addNotification(notification)
		},

		default: createTypedNotificationMethod('default'),
		error: createTypedNotificationMethod('error'),
		info: createTypedNotificationMethod('info'),
		success: createTypedNotificationMethod('success'),

		// Getter methods - direct access to composable properties
		getNotifications() {
			return notificationComposable.notifications
		},

		getErrorNotifications() {
			return notificationComposable.errorNotifications
		},

		getInfoNotifications() {
			return notificationComposable.infoNotifications
		},

		getSuccessNotifications() {
			return notificationComposable.successNotifications
		},
	},
}
